/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import {
  CreatePlanInput,
  ExecutionPolicy,
  PlanActionNotificationGroupScope,
  PlanActionNotificationTriggerType,
  PlanActionType,
  PlanNotificationTriggerType,
  SkipWindowType,
  useCreatePlanMutation,
  useDeleteGroupMutation,
} from "~/generated/graphql";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty, isNotNil } from "~/tools";
import {
  PlanData,
  PlanField,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import {
  NotificationGroupItem,
  NotificationGroupItemGeneral,
  SelectedPlanEntity,
  TimeUnits,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";
import {
  convertDateItemToDateString,
  convertToMinutes,
  prepareGroupInput,
} from "~/views/plans/utils";
import { useCreateResourceGroup } from "~/views/resourceGroups/hooks";
import { ResourceGroupData } from "~/views/resourceGroups/ResourceGroupCreate/components/ResourceGroupCreateWizard/constants";

type Props = {
  onSuccess: (id: string) => void;
};

export function useCreatePlan({ onSuccess }: Props) {
  const [createPlanMutation, { loading }] = useCreatePlanMutation({
    onCompleted: (data) => {
      if (data.createPlan) {
        onSuccess(data?.createPlan.id);
      }
    },
    onError: (error) => {
      showError(error.message);
    },
    update(cache, { data }, { variables }) {
      if (data?.createPlan && variables?.input) {
        cache.modify({
          fields: {
            plans: (existing) => {
              return {
                plans: [existing, variables.input],
              };
            },
          },
        });

        cache.gc();
      }
    },
  });

  const { cleanupResourceGroups } = useCleanupResourceGroups();
  const { createGroups } = useCreateGroups();

  async function createPlan(data: PlanData) {
    const groupIdsByListId = await createGroups(data);
    const createdGroupIds = Object.entries(groupIdsByListId).flatMap(
      ([_, groupIds]) => groupIds
    );

    const prepareData = mapPlanData(data, groupIdsByListId);
    try {
      createPlanMutation({
        variables: {
          input: prepareData,
        },
      }).then((response) => {
        if (response.errors) {
          cleanupResourceGroups(createdGroupIds);
        }
        return null;
      });
    } catch (error) {
      cleanupResourceGroups(createdGroupIds);
      showError(error.message);
    }
  }

  return { createPlan, loading } as const;
}

const getUnitMultiplier = (unit: string) => {
  switch (unit) {
    case "hours": {
      return 60;
    }
    case "minutes": {
      return 1;
    }
    case "days": {
      return 24 * 60;
    }
    default: {
      return 1;
    }
  }
};

function mapToActionWithNewResourceGroups<
  T extends { listId?: string; resourceGroupIds?: string[] }
>(entity: T, groupIdsByListId: Record<string, string[]>): T {
  const resourceGroupIds =
    entity.listId && groupIdsByListId[entity.listId]
      ? [...(entity.resourceGroupIds || []), ...groupIdsByListId[entity.listId]]
      : entity.resourceGroupIds;

  return {
    ...entity,
    resourceGroupIds,
  };
}

export function mapPlanData(
  data: PlanData,
  groupIdsByListId: Record<string, string[]>
): CreatePlanInput {
  const orderedPlanEntities = data.plan_settings?.planEntities?.map(
    (action, idx) => {
      const planAction = mapToActionWithNewResourceGroups(
        action,
        groupIdsByListId
      );

      const batchActions = action.batchActions?.map((batchAction) =>
        mapToActionWithNewResourceGroups(batchAction, groupIdsByListId)
      );

      return {
        ...planAction,
        batchActions,
        order: idx + 1,
      };
    }
  );

  const planActions = orderedPlanEntities
    ?.filter((action) => isEmpty(action.batchActions ?? []))
    .map(mapAction);

  const planActionBatches = orderedPlanEntities
    ?.filter((action) => !isEmpty(action.batchActions ?? []))
    .map(mapBatch);

  return {
    name: data.details?.name ?? "",
    description: data.details?.description,
    metadata: data.details?.metadata,
    enabled: data.plan_status?.planEnabled ?? false,
    scheduleOffset:
      Number(data.schedule_plan?.scheduleOffset ?? 0) *
      getUnitMultiplier(data.schedule_plan?.scheduleOffsetUnit ?? "minutes"),
    scheduleCron: data.schedule_plan?.scheduleCron,
    scheduleExecutionDates: data.schedule_plan?.scheduleExecutionDates?.map(
      (date) => {
        return dayjs(convertDateItemToDateString(date))
          .tz(data[PlanField.SCHEDULE_PLAN]?.scheduleTimezone, true)
          .toISOString();
      }
    ),
    scheduleTimezone: data.schedule_plan?.scheduleTimezone ?? "",
    planActions,
    planActionBatches,
    notificationGroups:
      data.general_notifications?.notificationGroupsGeneral?.flatMap(
        mapNotificationGroupItemGeneral
      ),
  };
}

type SelectedPlanEntityWithOrder = SelectedPlanEntity & { order: number };

const mapBatch = (batch: SelectedPlanEntityWithOrder) => {
  return {
    name: batch.name ?? "",
    order: batch.order,
    executionPolicy: batch.executionPolicy ?? ExecutionPolicy.Anyway,
    skipWindow: batch.skipWindow ?? SkipWindowType.Never,
    windowDuration: convertToMinutes(
      batch.windowDuration?.toString() ?? "",
      batch.unit ?? TimeUnits.minutes
    ),
    batchActions: batch.batchActions?.map((action, batchActionIdx) => {
      return mapBatchAction({ ...action, order: batchActionIdx + 1 });
    }),
  };
};

const mapAction = (action: SelectedPlanEntityWithOrder) => {
  return {
    ...mapBatchAction(action),
    executionPolicy: action.executionPolicy ?? ExecutionPolicy.Anyway,
    runInSequence: action.runInSequence,
    skipWindow: action.skipWindow ?? SkipWindowType.Never,
    windowDuration: convertToMinutes(
      action.windowDuration?.toString() ?? "",
      action.unit ?? TimeUnits.minutes
    ),
  };
};

const mapBatchAction = (action: SelectedPlanEntityWithOrder) => {
  return {
    name: action.name,
    action: {
      actionId: action.id ?? "",
      actionType: action.actionType ?? PlanActionType.System,
    },
    order: action.order,
    resourceGroupIds: action.resourceGroupIds,
    resourceIds: action.resourceIds,
    inputParameters: action.inputParameters,
    notificationGroups: action.notificationGroups
      ?.flatMap(mapNotificationGroupItem)
      .filter(isNotNil),
  };
};

const mapNotificationGroupItem = (
  notificationGroupItem: NotificationGroupItem
) => {
  return notificationGroupItem.notificationGroupIds?.map((id) => {
    return {
      notificationGroupId: id ?? "",
      scope:
        notificationGroupItem.scope ?? PlanActionNotificationGroupScope.Action,
      triggerEvent:
        notificationGroupItem.triggerEvent ??
        PlanActionNotificationTriggerType.Start,
      inAdvance: convertToMinutes(
        `${notificationGroupItem.inAdvance}` ?? 0,
        notificationGroupItem.inAdvanceUnit ?? TimeUnits.minutes
      ),
    };
  });
};

const mapNotificationGroupItemGeneral = (
  item: NotificationGroupItemGeneral
) => {
  return (
    item.notificationGroupIds?.map((id) => {
      return {
        notificationGroupId: id ?? "",
        triggerEvent: item.triggerEvent ?? PlanNotificationTriggerType.Start,
        inAdvance: convertToMinutes(
          item.inAdvance?.toString() ?? "",
          item.unit ?? TimeUnits.minutes
        ),
      };
    }) ?? []
  );
};

export function useCreateGroups() {
  const { createGroup } = useCreateResourceGroup({
    onSuccess: () => {},
  });

  const createGroups = async (
    data: PlanData
  ): Promise<Record<string, string[]>> => {
    const operations = createGroupOperations(data?.plan_settings?.planEntities);
    const results = operations.map(async (op) => {
      const newGroup = await createGroup(op.groupInput);
      const newGroupId = newGroup?.id;

      return { listId: op.listId, groupId: newGroupId ?? "" };
    });

    return Promise.all(results).then((resolvedResults) =>
      resolvedResults
        .filter((result) => result !== null)
        .reduce<Record<string, string[]>>((acc, { listId, groupId }) => {
          return {
            ...acc,
            [listId]: [...(acc[listId] || []), groupId],
          };
        }, {})
    );
  };

  return { createGroups };
}

type CreateGroupOperation = {
  listId: string;
  groupInput: ResourceGroupData;
};

export const createGroupOperations = (
  planEntities?: SelectedPlanEntity[]
): CreateGroupOperation[] => {
  if (!planEntities) {
    return [];
  }
  return planEntities.flatMap((planEntity) => {
    const isBatch = isNotEmpty(planEntity.batchActions ?? []);

    if (isBatch) {
      return (planEntity.batchActions || []).flatMap((action) =>
        (action.createResourceGroups || []).map((group) => ({
          listId: action.listId || action.id || "",
          groupInput: prepareGroupInput(group),
        }))
      );
    } else {
      return (planEntity.createResourceGroups || []).map((group) => ({
        listId: planEntity.listId || planEntity.id || "",
        groupInput: prepareGroupInput(group),
      }));
    }
  });
};

export const useCleanupResourceGroups = () => {
  const [deleteResourceGroup] = useDeleteGroupMutation();

  const cleanupResourceGroups = (groupIds: string[]) => {
    groupIds.forEach((id) => {
      deleteResourceGroup({
        variables: {
          id,
        },
      });
    });
  };
  return { cleanupResourceGroups };
};
